import { useState, Dispatch, SetStateAction, useEffect } from "react";
import { ChatMessageType, FetchChatMessagesByEntityIdQuery, TenderState, FetchChatMessagesByEntityIdDocument, FetchChatMessagesByEntityIdQueryVariables } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";

/**
 * Custom hook to manage polling for chat by entity id.
 *
 * This hook uses the `useFetchChatMessagesByEntityIdQuery` to poll the ChatMessages
 * Polling can be controlled via the `isPolling` state.
 *
 * @returns {Object} An object containing:
 * - `data`: The data returned from the query.
 * - `loading`: Boolean indicating if the query is currently loading.
 * - `isPolling`: Boolean indicating if polling is active.
 * - `startPolling`: Function to start polling.
 * - `stopPolling`: Function to stop polling.
 * - `setIsPolling`: Function to set the polling state.
 * - `useRefetch`: Function to refetch the query and update the Tender's state in cache.
 */
export const useFetchChatMessagesByEntityIdPolling = (tenderID: string): {
  data: FetchChatMessagesByEntityIdQuery | undefined,
  loading: boolean,
  isPolling: boolean,
  startPolling: (_pollInterval: number) => void,
  stopPolling: () => void,
  setIsPolling: Dispatch<SetStateAction<boolean>>;
  useRefetch: (_state: TenderState) => void
} => {
  const [isPolling, setIsPolling] = useState(true);

  const { data, loading, refetch, startPolling, stopPolling } = useQuery<FetchChatMessagesByEntityIdQuery, FetchChatMessagesByEntityIdQueryVariables>(
    FetchChatMessagesByEntityIdDocument, {
      variables: {
        entityId: Number(tenderID),
        type: ChatMessageType.Tender,
      },
      pollInterval: 5000, // Poll every 5 seconds
      skip: !isPolling,  // Control polling with state
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: false, // Prevents Apollo from updating the loading state during polling refetches
    });

  useEffect(() => {
    if (data) {
      setIsPolling(true);
    }
  }, [data]);

  const useRefetch = () => {
    refetch();
  };

  return { data, loading, isPolling, startPolling, stopPolling, setIsPolling, useRefetch };
};
